import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ExecucaoTimelineEvent {
  id: string;
  tipo_evento: string;
  descricao: string;
  dados: Record<string, unknown> | null;
  created_at: string;
  usuario_id: string | null;
  usuario_nome?: string | null;
}

/**
 * Fetches timeline events for a specific execution from:
 * 1. emp_eventos_negocio (glosa, anexo events)
 * 2. emp_workflow_transicoes (state transitions)
 */
export function useExecucaoTimeline(execucaoId: string | null) {
  return useQuery({
    queryKey: ['execucao-timeline', execucaoId],
    queryFn: async () => {
      if (!execucaoId) return [];

      const events: ExecucaoTimelineEvent[] = [];

      // 1. Business events (glosa, attachments)
      const { data: eventosNeg } = await supabase
        .from('emp_eventos_negocio')
        .select('id, tipo_evento, payload, created_at, user_id')
        .eq('entidade_id', execucaoId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (eventosNeg) {
        for (const e of eventosNeg) {
          const payload = (e.payload || {}) as Record<string, unknown>;
          events.push({
            id: e.id,
            tipo_evento: e.tipo_evento,
            descricao: (payload.descricao as string) || EVENT_TYPE_LABELS[e.tipo_evento] || e.tipo_evento,
            dados: payload,
            created_at: e.created_at,
            usuario_id: e.user_id,
          });
        }
      }

      // 2. Workflow transitions
      const { data: transicoes } = await supabase
        .from('emp_workflow_transicoes')
        .select('id, status_anterior, status_novo, observacoes, created_at, usuario_id, nome_usuario')
        .eq('execucao_id', execucaoId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (transicoes) {
        for (const t of transicoes) {
          events.push({
            id: t.id,
            tipo_evento: 'transicao_estado',
            descricao: `${formatStatus(t.status_anterior)} → ${formatStatus(t.status_novo)}${t.observacoes ? ` — ${t.observacoes}` : ''}`,
            dados: { status_anterior: t.status_anterior, status_novo: t.status_novo },
            created_at: t.created_at,
            usuario_id: t.usuario_id,
            usuario_nome: t.nome_usuario,
          });
        }
      }

      // Sort by date desc
      events.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      return events;
    },
    enabled: !!execucaoId,
    staleTime: 30_000,
  });
}

const STATUS_MAP: Record<string, string> = {
  em_apontamento: 'Em Apontamento',
  aguardando_qualidade: 'Aguardando Qualidade',
  aguardando_planejamento: 'Aguardando Planejamento',
  disponivel_para_medicao: 'Disponível para Medição',
  vinculada_a_boletim: 'Vinculada a Boletim',
  cancelado: 'Cancelado',
  rejeitado: 'Rejeitado',
};

function formatStatus(s: string | null): string {
  if (!s) return '—';
  return STATUS_MAP[s] || s;
}

export const EVENT_TYPE_LABELS: Record<string, string> = {
  transicao_estado: 'Transição',
  glosa_quantitativa_aplicada: 'Glosa Quantitativa',
  glosa_financeira_aplicada: 'Ajuste Financeiro',
  execucao_filha_criada: 'Reprocessamento',
  anexo_adicionado: 'Evidência Adicionada',
  anexo_removido: 'Evidência Removida',
};

export const EVENT_TYPE_COLORS: Record<string, string> = {
  transicao_estado: 'bg-blue-500',
  glosa_quantitativa_aplicada: 'bg-red-500',
  glosa_financeira_aplicada: 'bg-amber-500',
  execucao_filha_criada: 'bg-purple-500',
  anexo_adicionado: 'bg-emerald-500',
  anexo_removido: 'bg-muted-foreground',
};
